import { ArrowBack, Close } from '@mui/icons-material';
import { Box, Button, ButtonOwnProps, IconButton, Stack } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useSettingsSection } from '../Providers/SettingsSectionProvider';

interface ActionProps {
  onClick: () => void;
  label?: string;
  icon?: ButtonOwnProps['startIcon'];
  disabled?: boolean;
}

interface Props {
  children: React.ReactNode;
  header: React.ReactNode;
  /** Called in addition to closing the drawer when "x" is clicked in the header */
  onClose?: () => void;
  /** Adds a back button to the start of the header */
  onBack?: () => void;
  actions?: {
    reset?: ActionProps;
    confirm?: ActionProps;
  };
}
export default function SectionDrawerContent({ children, header, actions, onClose, onBack }: Props): React.JSX.Element {
  const { setDrawerOpen, isDrawerOpen } = useSettingsSection();

  // handled as an effect to capture all possible ways of closing the drawer
  useEffect(() => {
    if (!isDrawerOpen && onClose !== undefined) {
      onClose();
    }
  }, [isDrawerOpen]);

  const resetButton = useMemo(() => {
    if (actions?.reset === undefined) {
      return undefined;
    }

    return (
      <Button
        color={'inherit'}
        startIcon={actions.reset.icon}
        onClick={actions.reset.onClick}
        disabled={actions.reset.disabled === true}
      >
        {actions.reset.label ?? 'Reset'}
      </Button>
    );
  }, [actions?.reset]);

  const confirmButton = useMemo(() => {
    if (actions?.confirm === undefined) {
      return undefined;
    }

    return (
      <Button
        variant={'contained'}
        endIcon={actions.confirm.icon}
        onClick={actions.confirm.onClick}
        disabled={actions.confirm.disabled === true}
      >
        {actions.confirm.label ?? 'Confirm'}
      </Button>
    );
  }, [actions?.confirm]);

  const actionsContainer = useMemo(() => {
    if ([resetButton, confirmButton].every((b) => b === undefined)) {
      return <></>;
    }

    return (
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} padding={2}>
          {resetButton}
          {confirmButton}
        </Stack>
      </Box>
    );
  }, [confirmButton, resetButton]);

  const headerContainer = useMemo(
    () => (
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} spacing={2} padding={2}>
          {onBack && (
            <IconButton size={'small'} onClick={onBack}>
              <ArrowBack fontSize={'small'} />
            </IconButton>
          )}
          <Box width={'100%'}>{header}</Box>
          <IconButton onClick={() => setDrawerOpen(false)} size={'small'} aria-label={'Close'}>
            <Close fontSize={'small'} />
          </IconButton>
        </Stack>
      </Box>
    ),
    [header, onBack],
  );

  return (
    <Stack direction={'column'} spacing={2} sx={{ height: '100vh' }}>
      {headerContainer}
      <Box flex={1} overflow={'auto'} sx={{ px: 2 }}>
        {children}
      </Box>
      {actionsContainer}
    </Stack>
  );
}
